
import { Component, Mixins, Watch } from "vue-property-decorator";
import OfficeProfileMixin from "../../office-profiles.mixin";
import { StripeCheckout, StripeElementCard } from "@vue-stripe/vue-stripe";
import AuthMixin from "@/common/mixins/auth.mixin";
import StripeService from "@/services/stripe.service";
import { CheckoutRequestDto, StripeInfoDto, StripePaymentMethodDto } from "@/types/stripe.type";
import CreateEditPaymentMethodModal from "../create-edit-payment-method-modal.vue";
import { mapState } from "vuex";
import { OfficeDataDto } from "@/types/office.type";
import { SubscriptionStatus } from "@/common/constants";
import { formatDate } from "@/utils/date.util";

@Component({
  components: {
    StripeCheckout,
    StripeElementCard,
    CreateEditPaymentMethodModal
  },
  computed: {
    ...mapState("OfficeProfile", ["officeData"])
  }
})
export default class OfficePaymentEditComponent extends Mixins(OfficeProfileMixin, AuthMixin) {
  officeData!: OfficeDataDto | null;

  sessionId: string | null = null;
  info: StripeInfoDto | null = null;
  selectedPaymentMethodId: string | null = null;

  columnCount = 3;
  loading = true;
  stripePublishableKey = process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY;
  dateOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric', month: 'short', day: 'numeric'
  };

  formatDate = formatDate;

  get columnStyle(): string {
    return `width: ${100 / this.columnCount}%;`;
  }

  get splitPaymentMethods(): StripePaymentMethodDto[][] {
    const splitPaymentMethods: StripePaymentMethodDto[][] = [];
    
    let currentColumn = 0;

    this.info?.paymentMethods.forEach(paymentMethod => {
      if (!splitPaymentMethods[currentColumn]) {
        splitPaymentMethods[currentColumn] = [];
      }

      splitPaymentMethods[currentColumn].push(paymentMethod);

      if (currentColumn > 0 && currentColumn % (this.columnCount - 1) === 0) {
        currentColumn = 0;

        return;
      }

      currentColumn++;
    });

    return splitPaymentMethods;
  }

  get trialingEndedSubscriptionStatusId(): number | null {
    if (!this.officeData) {
      return null;
    }

    return SubscriptionStatus.TrialingEnded.getId(this.officeData.subscriptionStatuses);
  }

  get trialingSubscriptionStatusId(): number | null {
    if (!this.officeData) {
      return null;
    }

    return SubscriptionStatus.Trialing.getId(this.officeData.subscriptionStatuses);
  }

  private onPageShow = (evt: any) => {
    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    const fromBFCache = (evt && evt.persisted === true) || nav?.type === 'back_forward';
    if (fromBFCache) {
      this.sessionId = null;
      this.loading = false;
      this.getInfo();
    }
  };

  async mounted(): Promise<void> {
    window.addEventListener('pageshow', this.onPageShow);
    await this.getInfo();
  }

  beforeDestroy() {
    window.removeEventListener('pageshow', this.onPageShow);
  }

  @Watch('$route.query.status')
  onStatusChange(status: string) {
    if (status === 'cancel') {
      this.sessionId = null;
      this.loading = false;
    } else if (status === 'success') {
      this.loading = false;
      this.getInfo();
    }
  }

  async getInfo(): Promise<void> {
    if (!this.selectedOffice) {
      return;
    }
    this.loading = true;
    try {
      this.info = await StripeService.getInfo(this.selectedOffice.id, process.env.VUE_APP_STRIPE_ACTIVE_PRODUCT_ID);
    } catch (e) {
    } finally {
      this.loading = false;
    }
  }

  async renew(): Promise<void> {
    if (!this.info?.activeSubscription || !this.selectedOffice) {
      return;
    }
    this.loading = true;
    try {
      await StripeService.renewSubscription(this.info.activeSubscription.id, this.selectedOffice.id);
      await this.getInfo();
    } catch (e) {
    } finally {
      this.loading = false;
    }
  }

  async cancel(): Promise<void> {
    if (!this.info?.activeSubscription || !this.selectedOffice) {
      return;
    }
    this.loading = true;
    try {
      await StripeService.cancelSubscription(this.info.activeSubscription.id, this.selectedOffice.id);
      await this.getInfo();
    } catch (e) {
    } finally {
      this.loading = false;
    }
  }

  async checkout(): Promise<void> {
    const userInfo = this.userInfo();
    if (!this.selectedOffice || !userInfo) {
      return;
    }
    this.loading = true;
    try {
      this.sessionId = await StripeService.createCheckoutSession({
        customerId: this.selectedOffice.stripeCustomerId,
        officeId: this.selectedOffice.id,
        email: userInfo.email
      } as CheckoutRequestDto);
      (this.$refs.checkout as any).redirectToCheckout();
    } catch (e) {
      this.loading = false;
    }
  }

  showCreateEditPaymentMethod(id: string | null = null): void {
    this.selectedPaymentMethodId = id;

    this.$bvModal.show("create-edit-payment-method-modal");
  }
}
